import { type CSSProperties, type ReactElement } from 'react'
import { Center, SegmentedControl, useMantineColorScheme } from '@mantine/core'
import { type MaterialSymbolType, type SelectItem, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '../../terros/icon/MaterialSymbolIcon'

const COLOR_SCHEME_OPTIONS = ['light', 'dark', 'auto'] as const
type ColorScheme = (typeof COLOR_SCHEME_OPTIONS)[number]

type AppearanceOption = SelectItem<ColorScheme> & {
  icon: MaterialSymbolType
}

const APPEARANCE_OPTIONS: AppearanceOption[] = [
  { value: 'light', label: 'Light', icon: 'light_mode' },
  { value: 'dark', label: 'Dark', icon: 'dark_mode' },
  { value: 'auto', label: 'System', icon: 'monitor' },
]

const isColorScheme = (value: string): value is ColorScheme => COLOR_SCHEME_OPTIONS.some((option) => option === value)

const fullWidthStyle: CSSProperties = { width: '100%' }

type AppearanceControlProps = {
  fullWidth?: boolean
}

export const AppearanceControl = ({ fullWidth = false }: AppearanceControlProps): ReactElement => {
  const { t } = useTranslation()
  const { colorScheme, setColorScheme } = useMantineColorScheme()

  return (
    <SegmentedControl
      aria-label={t('Appearance')}
      data={APPEARANCE_OPTIONS.map((option) => ({
        value: option.value,
        label: (
          <Center aria-label={t(option.label)} title={t(option.label)}>
            <MaterialSymbolIcon icon={option.icon} size='lg' filled={colorScheme === option.value} />
          </Center>
        ),
      }))}
      fullWidth={fullWidth}
      style={fullWidth ? fullWidthStyle : undefined}
      value={colorScheme}
      onChange={(value) => {
        if (!isColorScheme(value)) return
        setColorScheme(value)
      }}
    />
  )
}
